import { isSkipped } from './contentRoot';
import { controlName, describeControl, isMuiSelect } from './descriptors';
import { REDACTED, isSensitiveField, redactText } from './redaction';
import { visibleText } from './textBlocks';
import { StyleView } from './types';

export type ControlSections = { inputs: string; actions: string };

const MAX_VALUE = 300;
const MAX_ROW_LABEL = 60;

const BUTTON_INPUT_TYPES = new Set(['submit', 'button', 'reset', 'image']);
const ACTION_SELECTOR = 'button,a[href],[role="button"],[role="tab"],[role="menuitem"],[role="link"]';

const quote = (text: string): string => `"${text.replace(/"/g, '\\"')}"`;

const truncate = (text: string): string => (text.length > MAX_VALUE ? `${text.slice(0, MAX_VALUE)}…` : text);

const isDisabled = (el: Element): boolean => (
  el.hasAttribute('disabled')
  || el.getAttribute('aria-disabled') === 'true'
  || el.classList.contains('Mui-disabled')
);

const flags = (el: Element): string => [
  isDisabled(el) ? 'disabled' : '',
  el.hasAttribute('readonly') || el.getAttribute('aria-readonly') === 'true' ? 'read-only' : '',
  el.hasAttribute('required') || el.getAttribute('aria-required') === 'true' ? 'required' : '',
].filter(Boolean).map((flag) => ` (${flag})`).join('');

const nameSuffix = (el: Element, label: string): string => {
  const name = controlName(el);
  return name && name !== label ? ` (name=${name})` : '';
};

// The raw value is cut before any regex runs, so a multi-megabyte textarea cannot stall the read.
const rawValue = (el: Element, view: StyleView): string => {
  if (isMuiSelect(el)) return visibleText(el, view, true);
  if (el instanceof HTMLSelectElement) {
    return Array.from(el.selectedOptions).map((option) => option.text.trim()).join(', ');
  }
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return el.value.slice(0, MAX_VALUE * 4);
  return '';
};

// An empty field is reported as empty even when sensitive: a missing SSN is useful to know and not a secret.
const fieldValue = (el: Element, view: StyleView, label: string): string => {
  const raw = rawValue(el, view);
  if (raw.trim() === '') return '';
  return isSensitiveField(el, label) ? REDACTED : redactText(raw);
};

const fieldRole = (el: Element): string => {
  if (isMuiSelect(el) || el.tagName === 'SELECT') return 'combobox';
  const type = (el.getAttribute('type') ?? '').toLowerCase();
  if (type === 'checkbox' || type === 'radio') return type;
  if (type === 'file') return 'file input';
  return el.getAttribute('role') ?? 'textbox';
};

// A field in a table row is told apart from its siblings by the first text cell of that row.
const rowSuffix = (el: Element, root: Element, view: StyleView, cache: WeakMap<Element, string>): string => {
  const row = el.closest('tr,[role="row"]');
  if (!row || !root.contains(row)) return '';

  const cached = cache.get(row);
  if (cached !== undefined) return cached;

  const text = Array.from(row.children)
    .filter((cell) => !cell.contains(el))
    .map((cell) => visibleText(cell, view))
    .find((cellText) => cellText !== '') ?? '';
  const suffix = text === '' ? '' : ` (row: ${quote(text.length > MAX_ROW_LABEL ? `${text.slice(0, MAX_ROW_LABEL)}…` : text)})`;
  cache.set(row, suffix);
  return suffix;
};

const describeField = (el: Element, view: StyleView, rowContext: string): string => {
  const label = describeControl(el, view);
  const role = fieldRole(el);
  const prefix = `- ${role} ${quote(label)}${rowContext}${nameSuffix(el, label)}`;

  if (role === 'checkbox' || role === 'radio') {
    return `${prefix} = ${(el as HTMLInputElement).checked ? 'checked' : 'unchecked'}${flags(el)}`;
  }
  if (role === 'file input') return `${prefix}${flags(el)}`;

  const value = truncate(fieldValue(el, view, label).replace(/\s+/g, ' ').trim());
  return `${prefix} = ${value === '' ? '(empty)' : value}${flags(el)}`;
};

const actionRole = (el: Element): string => {
  const role = el.getAttribute('role');
  if (role) return role;
  if (el.tagName === 'A') return 'link';
  return 'button';
};

const describeAction = (el: Element, view: StyleView): string => {
  const text = el instanceof HTMLInputElement
    ? el.value
    : visibleText(el, view, true);
  const label = text || el.getAttribute('aria-label') || el.getAttribute('title') || '(unlabeled)';
  const selected = el.getAttribute('aria-selected') === 'true' ? ' (selected)' : '';
  return `- ${actionRole(el)} ${quote(truncate(label))}${isDisabled(el) ? ' (disabled)' : ''}${selected}`;
};

const isField = (el: Element): boolean => {
  if (isMuiSelect(el)) return true;
  if (el.tagName === 'SELECT' || el.tagName === 'TEXTAREA') return true;
  if (el.tagName !== 'INPUT') return false;
  const type = (el.getAttribute('type') ?? 'text').toLowerCase();
  return type !== 'hidden' && !BUTTON_INPUT_TYPES.has(type);
};

const isAction = (el: Element): boolean => (
  el.matches(ACTION_SELECTOR)
  || (el.tagName === 'INPUT' && BUTTON_INPUT_TYPES.has((el.getAttribute('type') ?? '').toLowerCase()))
);

/**
 * Lists the visible form fields (with their live values) and the actionable elements under `root`.
 * A field or action is reported once; its subtree is not searched further.
 */
export const serializeControls = (root: Element, view: StyleView): ControlSections => {
  const inputs: string[] = [];
  const actions: string[] = [];
  const rowLabels = new WeakMap<Element, string>();

  const visit = (el: Element) => {
    if (isSkipped(el, view)) return;
    if (isField(el)) {
      inputs.push(describeField(el, view, rowSuffix(el, root, view, rowLabels)));
      return;
    }
    if (isAction(el)) {
      actions.push(describeAction(el, view));
      return;
    }
    Array.from(el.children).forEach(visit);
  };

  visit(root);
  return { inputs: inputs.join('\n'), actions: actions.join('\n') };
};
